import { GenericRemoteActionBuilder } from '../remote-action-builder';
import { Injectable, ViewContainerRef, ComponentRef } from '@angular/core';
import { LoggingService } from 'src/app/_services/logging/logic/logging.service';
import { ClientStateService } from 'src/app/_services/client-state/logic/client-state.service';
import { RemoteAction } from '../../remote-action';
import { TimeCorrectionRemoteActionBuilderUiStep } from '../../remote-action-builder-steps/time-correction-remote-action-builder-ui-step/time-correction-remote-action-builder-ui-step';
import { TimeCorrectionComponent } from 'src/app/_components/remote-action-step/time-correction/time-correction.component';

import { LocationService } from 'src/app/_services/location/location.service';
import { WorktypeSelectRemoteActionBuilderUIStep } from '../../remote-action-builder-steps/worktype-select-remote-action-builder-ui-step/worktype-select-remote-action-builder-ui-step';
import { PlaceTypeRemoteActionBuilderServiceStep } from '../../remote-action-builder-steps/placetype-remote-action-builder-service-step/placetype-remote-action-builder-service-step';
import { ServicejobIdRemoteActionBuilderServiceStep } from '../../remote-action-builder-steps/servicejob-id-remote-action-builder-service-step/servicejob-id-remote-action-builder-service-step';
import { AppService } from 'src/app/_services/app-service/logic/app.service';
import { CredentialsService } from 'src/app/_services/credentials/logic/credentials.service';
import { ServicejobsService } from 'src/app/_services/servicejobs/logic/servicejobs.service';
import { GpsRemoteActionBuilderServiceStep } from '../../remote-action-builder-steps/gps-remote-action-builder-service-step/gps-remote-action-builder-service-step';
import { EmpSelectRemoteActionBuilderUiStep } from '../../remote-action-builder-steps/emp-select-remote-action-builder-ui-step/emp-select-remote-action-builder-ui-step';
import { AppParameterService } from 'src/app/_services/app-parameter/logic/app-parameter.service';
import { EmployeeService } from 'src/app/_services/employee/logic/employee.service';
import { IMSId, IMSObject } from 'src/app/_model/ims-object';
import { WorktypesService } from 'src/app/_services/worktypes/logic/worktypes.service';
import { TranslateService } from '@ngx-translate/core';
import { EventService } from 'src/app/_services/event/event.service';
import { SYSCODE_DEFAULT_BAD_WEATHER } from 'src/app/_model/workttype';

@Injectable()
export class WorkTypeRemoteActionBuilder extends GenericRemoteActionBuilder {
  constructor(
    appService: AppService,
    credentialsService: CredentialsService,
    servicejobService: ServicejobsService,
    locationService: LocationService,
    private clientStateService: ClientStateService,
    private employeeService: EmployeeService,
    private appParameterService: AppParameterService,
    private worktypesService: WorktypesService,
    private translate: TranslateService,
    private logger?: LoggingService,
    private specificWorkType?: IMSObject,
    private endCurrentWorktype: boolean = false
  ) {
    super(
      'WORKTYPE-REMOTEACTION.PAGE-TITLE',
      appService,
      credentialsService,
      {
        endpoint: 'changeworktype',
        data: {},
        cachable: true,
        fifo: true,
        authenticationRequired: true,
        shortLabel: translate.instant('WORKTYPE-REMOTEACTION.RA.LABEL.SHORT', {
          worktypelabel:
            specificWorkType != undefined
              ? specificWorkType.displayLabel
              : translate.instant('WORKTYPE-REMOTEACTION.DEFAULTWORKTYPELABEL'),
        }),
        shortLabelTranslate: false,
      },
      []
        .concat(
          appParameterService.appParameters$
            .getValue()
            ?.getBooleanParameter(
              'imsmc.worktypeselect.showempselection',
              true
            ) && !employeeService.onlyMeInTeam()
            ? [new EmpSelectRemoteActionBuilderUiStep(logger)]
            : []
        )
        .concat(
          specificWorkType != undefined || endCurrentWorktype
            ? []
            : [new WorktypeSelectRemoteActionBuilderUIStep(logger)]
        )
        .concat([new TimeCorrectionRemoteActionBuilderUiStep(logger)]),
      [
        new PlaceTypeRemoteActionBuilderServiceStep(clientStateService, logger),
        new ServicejobIdRemoteActionBuilderServiceStep(
          clientStateService,
          servicejobService,
          logger
        ),
        new GpsRemoteActionBuilderServiceStep(locationService, logger),
      ],
      async (data: any) => {
        logger.log(
          this.constructor.name,
          ' Executing post generate function',
          data
        );
        const wtId: IMSId = {
          imsId: data['wtid'],
          secondaryId: undefined,
          objRef: 'ims/WorkType',
        };
        const wt = this.worktypesService.getWorktypeById(wtId);
        const empList = data['emps']?.split(';');
        const curTeam = this.employeeService.getTeamIDs();

        if (
          this.appParameterService.appParameters$
            .getValue()
            ?.getBooleanParameter(
              'imsmc.worktypeselect.usecustomworktypestate',
              false
            )
        ) {
          if (endCurrentWorktype) {
            logger.log(
              this.constructor.name,
              'End Current Custom Worktype on Clientstate'
            );
            await clientStateService.stopCustomWorkType();
          } else {
            logger.log(
              this.constructor.name,
              'Update Custom Worktype on Clientstate'
            );
            
            await clientStateService.startCustomWorkType(
              specificWorkType != undefined ? specificWorkType.id : wtId
            );
          }
        }

        if (
          (empList == null 
          || empList.length === 0 
          || this.employeeService.onlyMeInTeam()
          || curTeam.length === empList.length)
          && wt.absenttime && wt.syscode === SYSCODE_DEFAULT_BAD_WEATHER
        ) {
          this.clientStateService.stopWorking();
        }
      },
      logger
    );
  }

  public async next(dataValues?: { key: string; value: any }[]): Promise<any> {
    return super.next(dataValues);
  }

  public async generateRemoteAction(
    dataValues: { key: string; value: any }[]
  ): Promise<RemoteAction> {
    if (this.specificWorkType) {
      dataValues.push({
        key: 'wtid',
        value: this.specificWorkType?.id?.imsId,
      });
      dataValues.push({
        key: 'wtlabel',
        value: this.specificWorkType?.label,
      });
    }
    if (this.endCurrentWorktype) {
      let defaultWorkType: IMSObject;

      if (
        this.clientStateService.theState.getValue()?.serviceJobId != undefined
      ) {
        defaultWorkType = this.worktypesService.getDefaultCustomerWorktype();
      } else {
        defaultWorkType = this.worktypesService.getDefaultWorktype();
      }

      dataValues.push({
        key: 'wtid',
        value: defaultWorkType.id?.imsId,
      });
      dataValues.push({
        key: 'wtlabel',
        value: defaultWorkType.label,
      });
    }
    return super.generateRemoteAction(dataValues);
  }

  public generateComponent(
    viewContainerRef: ViewContainerRef
  ): ComponentRef<any> {
    const componentRef = super.generateComponent(viewContainerRef);

    if (componentRef.instance instanceof TimeCorrectionComponent) {
      const worktypeLabel =
        this.specificWorkType != undefined
          ? this.specificWorkType.label
          : this.translate.instant(
              'WORKTYPE-REMOTEACTION.DEFAULTWORKTYPELABEL'
            );
      this.logger.log(
        this.constructor.name,
        'Generate Component of Type TimeCorrectionComponent report worktype'
      );

      (<TimeCorrectionComponent>componentRef.instance).setDescription(
        this.translate.instant('WORKTYPE-REMOTEACTION.DESCRIPTION', {
          worktypelabel: this.customTitle ?? worktypeLabel,
        }),
        false
      );
      (<TimeCorrectionComponent>componentRef.instance).setTitle(
        this.translate.instant('WORKTYPE-REMOTEACTION.TITLE', {
          worktypelabel: worktypeLabel,
        }),
        false
      );

      if (this.getRemoteActionData()['wtLabel']) {
        (<TimeCorrectionComponent>componentRef.instance).setSubtitle(
          this.getRemoteActionData()['wtLabel'],
          false
        );
      }

      const appParams = this.appParameterService.appParameters$.getValue();
      if (appParams != undefined) {
        (<TimeCorrectionComponent>componentRef.instance).hideComment =
          appParams.getBooleanParameter(
            'imsmc.remoteaction-step.hidecomment.wtchange',
            false
          );
      }
    }

    return componentRef;
  }
}
